import { useState } from "react";
import { useApp } from "../context/AppContext";
import { DraftSettings, PokemonType, Tier } from "../types/pokemon";
import { validatePool } from "../utils/draftPool";
import { translateType } from "../utils/typeUtils";

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ALL_GENERATIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const ALL_TYPES: PokemonType[] = [
  "normal", "fire", "water", "electric", "grass", "ice",
  "fighting", "poison", "ground", "flying", "psychic", "bug",
  "rock", "ghost", "dragon", "dark", "steel", "fairy",
];

const TIERS: { value: Tier; label: string }[] = [
  { value: "all", label: "Tous" },
  { value: "ou", label: "OU" },
  { value: "uu", label: "UU" },
  { value: "ag", label: "AG" },
];

export function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const { state, dispatch } = useApp();

  const [local, setLocal] = useState<DraftSettings>({ ...state.settings });

  if (!isOpen) return null;

  const validation = validatePool(local);

  const toggleGeneration = (gen: number) => {
    setLocal(prev => {
      const enabled = prev.enabledGenerations.includes(gen)
        ? prev.enabledGenerations.filter(g => g !== gen)
        : [...prev.enabledGenerations, gen].sort((a, b) => a - b);
      return { ...prev, enabledGenerations: enabled };
    });
  };

  const handleSave = () => {
    dispatch({ type: "UPDATE_SETTINGS", settings: local });
    onClose();
  };

  const handleCancel = () => {
    setLocal({ ...state.settings });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backdropFilter: "blur(6px)", backgroundColor: "rgba(0,0,0,0.75)" }}
      onClick={(e) => { if (e.target === e.currentTarget) handleCancel(); }}
    >
      <div
        className="relative w-full max-w-md mx-4 rounded-2xl overflow-y-auto"
        style={{
          maxHeight: "90vh",
          background: "linear-gradient(135deg, #1A4675 0%, #0f2a47 100%)",
          border: "4px solid #3B82C8",
          boxShadow: "0 0 0 2px #1A4675, 0 16px 40px rgba(0,0,0,0.8), inset 0 2px 0 rgba(107,183,232,0.4)",
        }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-poke-blue/40">
          <h2 className="font-pokemon text-xs text-poke-yellow text-center tracking-widest">
            Reglages
          </h2>
        </div>

        <div className="px-6 py-4 flex flex-col gap-5">

          {/* Generations */}
          <section>
            <p className="font-pokemon text-[10px] text-poke-blue-light mb-3 tracking-wider">
              Generations
            </p>
            <div className="flex flex-wrap gap-2">
              {ALL_GENERATIONS.map(gen => {
                const active = local.enabledGenerations.includes(gen);
                return (
                  <button
                    key={gen}
                    onClick={() => toggleGeneration(gen)}
                    className="font-pokemon text-[10px] px-3 py-2 rounded-lg border-2 transition-all hover:scale-105"
                    style={{
                      backgroundColor: active ? "#FFDE00" : "transparent",
                      borderColor: active ? "#FFB300" : "#3B82C8",
                      color: active ? "#1A4675" : "#6BB7E8",
                      boxShadow: active
                        ? "0 0 8px rgba(255,222,0,0.5), inset 0 1px 0 rgba(255,255,255,0.3)"
                        : "inset 0 1px 0 rgba(107,183,232,0.2)",
                    }}
                  >
                    {gen}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Legendary / Mythical */}
          <section>
            <p className="font-pokemon text-[10px] text-poke-blue-light mb-3 tracking-wider">
              Pokemon speciaux
            </p>
            <div className="flex flex-col gap-2">
              {[
                { key: "allowLegendaries" as const, label: "Legendaires" },
                { key: "allowMythicals" as const, label: "Mythiques" },
              ].map(({ key, label }) => (
                <label
                  key={key}
                  className="flex items-center gap-3 cursor-pointer group"
                >
                  <div
                    className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all"
                    style={{
                      borderColor: local[key] ? "#FFDE00" : "#3B82C8",
                      backgroundColor: local[key] ? "#FFDE00" : "transparent",
                    }}
                    onClick={() => setLocal(prev => ({ ...prev, [key]: !prev[key] }))}
                  >
                    {local[key] && (
                      <span style={{ color: "#1A4675", fontSize: "12px", lineHeight: 1 }}>✓</span>
                    )}
                  </div>
                  <span
                    className="font-pokemon text-[10px] tracking-wide"
                    style={{ color: local[key] ? "#FFDE00" : "#6BB7E8" }}
                    onClick={() => setLocal(prev => ({ ...prev, [key]: !prev[key] }))}
                  >
                    {label}
                  </span>
                </label>
              ))}
            </div>
          </section>

          {/* Type filter */}
          <section>
            <p className="font-pokemon text-[10px] text-poke-blue-light mb-3 tracking-wider">
              Filtre par type
            </p>
            <select
              value={local.typeFilter ?? ""}
              onChange={e => setLocal(prev => ({ ...prev, typeFilter: e.target.value || null }))}
              className="w-full rounded-lg px-3 py-2 font-pokemon text-[10px] outline-none"
              style={{
                backgroundColor: "#0f2a47",
                border: "2px solid #3B82C8",
                color: "#6BB7E8",
              }}
            >
              <option value="">Aucun</option>
              {ALL_TYPES.map(type => (
                <option key={type} value={type}>
                  {translateType(type)}
                </option>
              ))}
            </select>
          </section>

          {/* Cards per draw */}
          <section>
            <p className="font-pokemon text-[10px] text-poke-blue-light mb-3 tracking-wider">
              Cartes par tirage
            </p>
            <div className="flex gap-2">
              {([3, 4, 5] as const).map(n => {
                const active = local.cardsPerDraw === n;
                return (
                  <button
                    key={n}
                    onClick={() => setLocal(prev => ({ ...prev, cardsPerDraw: n }))}
                    className="flex-1 font-pokemon text-[10px] py-2 rounded-lg border-2 transition-all hover:scale-105"
                    style={{
                      backgroundColor: active ? "#FFDE00" : "transparent",
                      borderColor: active ? "#FFB300" : "#3B82C8",
                      color: active ? "#1A4675" : "#6BB7E8",
                      boxShadow: active ? "0 0 8px rgba(255,222,0,0.5)" : undefined,
                    }}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Max rerolls */}
          <section>
            <div className="flex justify-between items-center mb-3">
              <p className="font-pokemon text-[10px] text-poke-blue-light tracking-wider">
                Rerolls max
              </p>
              <span className="font-pokemon text-[10px] text-poke-yellow">
                {local.maxRerolls}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={10}
              value={local.maxRerolls}
              onChange={e => setLocal(prev => ({ ...prev, maxRerolls: Number(e.target.value) }))}
              className="w-full accent-poke-yellow"
              style={{ accentColor: "#FFDE00" }}
            />
            <div className="flex justify-between mt-1">
              <span className="font-pokemon text-[8px] text-poke-blue-light/60">0</span>
              <span className="font-pokemon text-[8px] text-poke-blue-light/60">10</span>
            </div>
          </section>

          {/* Tier */}
          <section>
            <p className="font-pokemon text-[10px] text-poke-blue-light mb-3 tracking-wider">
              Tier
            </p>
            <div className="flex gap-2">
              {TIERS.map(({ value, label }) => {
                const active = local.tier === value;
                return (
                  <button
                    key={value}
                    onClick={() => setLocal(prev => ({ ...prev, tier: value }))}
                    className="flex-1 font-pokemon text-[10px] py-2 rounded-lg border-2 transition-all hover:scale-105"
                    style={{
                      backgroundColor: active ? "#FFDE00" : "transparent",
                      borderColor: active ? "#FFB300" : "#3B82C8",
                      color: active ? "#1A4675" : "#6BB7E8",
                      boxShadow: active ? "0 0 8px rgba(255,222,0,0.5)" : undefined,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Blind Mode */}
          <section>
            <label className="flex items-center gap-3 cursor-pointer group">
              <div
                className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all shrink-0"
                style={{
                  borderColor: local.blindMode ? "#FFDE00" : "#3B82C8",
                  backgroundColor: local.blindMode ? "#FFDE00" : "transparent",
                }}
              >
                {local.blindMode && <span className="text-[10px] text-poke-blue-darker font-bold">✓</span>}
              </div>
              <div>
                <span className="font-pokemon text-[10px] text-poke-blue-light group-hover:text-white transition-colors">
                  Mode Aveugle
                </span>
                <p className="font-display text-[9px] text-white/40 mt-0.5">
                  Pokemon caches. Cliquer pour reveler puis selectionner.
                </p>
              </div>
              <input
                type="checkbox"
                className="sr-only"
                checked={local.blindMode}
                onChange={() => setLocal(prev => ({ ...prev, blindMode: !prev.blindMode }))}
              />
            </label>

            {/* Blind clues selection - only visible when blind mode is on */}
            {local.blindMode && (
              <div className="mt-3 ml-8 flex flex-col gap-2 animate-fade-in">
                <p className="font-pokemon text-[9px] text-white/40 mb-1">Indices visibles :</p>
                {([
                  { key: "showId" as const, label: "Numero Pokedex", emoji: "#" },
                  { key: "showTypes" as const, label: "Type(s)", emoji: "◆" },
                  { key: "showGeneration" as const, label: "Generation", emoji: "★" },
                  { key: "showCategory" as const, label: "Categorie", emoji: "≡" },
                  { key: "showDescription" as const, label: "Description Pokedex", emoji: "📖" },
                ]).map(({ key, label, emoji }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer group/clue">
                    <div
                      className="w-4 h-4 rounded border flex items-center justify-center transition-all shrink-0"
                      style={{
                        borderColor: local.blindClues[key] ? "#FFDE00" : "#3B82C880",
                        backgroundColor: local.blindClues[key] ? "#FFDE0030" : "transparent",
                      }}
                    >
                      {local.blindClues[key] && <span className="text-[8px] text-poke-yellow">✓</span>}
                    </div>
                    <span className="text-[10px] text-white/30 w-4">{emoji}</span>
                    <span className="font-display text-[10px] text-white/50 group-hover/clue:text-white/80 transition-colors">
                      {label}
                    </span>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={local.blindClues[key]}
                      onChange={() => setLocal(prev => ({
                        ...prev,
                        blindClues: { ...prev.blindClues, [key]: !prev.blindClues[key] },
                      }))}
                    />
                  </label>
                ))}
              </div>
            )}
          </section>

          {/* Pool validation warning */}
          {!validation.valid && (
            <div
              className="rounded-lg px-4 py-3 flex items-start gap-2"
              style={{
                backgroundColor: "rgba(238,21,21,0.15)",
                border: "2px solid rgba(238,21,21,0.6)",
              }}
            >
              <span className="text-poke-red-light text-sm mt-0.5">⚠</span>
              <p className="font-pokemon text-[9px] text-poke-red-light leading-relaxed">
                {validation.message}
              </p>
            </div>
          )}

          {validation.valid && (
            <p className="font-pokemon text-[9px] text-green-400 text-center">
              Pool: {validation.count} Pokemon disponibles
            </p>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-2 pb-2">
            <button
              onClick={handleCancel}
              className="flex-1 font-pokemon text-[10px] py-3 rounded-lg border-2 transition-all hover:scale-105"
              style={{
                backgroundColor: "transparent",
                borderColor: "#3B82C8",
                color: "#6BB7E8",
              }}
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={!validation.valid}
              className="flex-1 font-pokemon text-[10px] py-3 rounded-lg border-2 transition-all hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{
                backgroundColor: validation.valid ? "#FFDE00" : "transparent",
                borderColor: validation.valid ? "#FFB300" : "#3B82C8",
                color: validation.valid ? "#1A4675" : "#6BB7E8",
                boxShadow: validation.valid ? "0 0 12px rgba(255,222,0,0.4)" : undefined,
              }}
            >
              Sauvegarder
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
